import ipaddr from "ipaddr.js";
import { validateDns } from "./schema.ts";

export interface Network {
  json(url: URL, signal: AbortSignal): Promise<unknown>;
  text(url: URL, signal: AbortSignal): Promise<string>;
  txt(name: string, signal: AbortSignal): Promise<string[][]>;
}

export function publicAddress(address: string): boolean {
  try {
    return ipaddr.parse(address).range() === "unicast";
  } catch {
    return false;
  }
}

export function publicUrl(url: URL): void {
  if (
    url.protocol !== "https:" || url.username || url.password || url.hash ||
    url.hostname.endsWith(".bsky.app") || url.hostname === "bsky.app"
  ) {
    throw new Error(
      "Only public HTTPS identity and PDS endpoints are allowed.",
    );
  }
  const host = url.hostname.replace(/^\[|\]$/g, "");
  if (
    ipaddr.isValid(host) ? !publicAddress(host) : !host.includes(".") ||
      /\.(localhost|local|internal|test|invalid)$/.test(host)
  ) {
    throw new Error("Private network endpoints are not allowed.");
  }
}

export class BrowserNetwork implements Network {
  #cache = new Map<string, { text: string; expires: number; bytes: number }>();
  #bytes = 0;

  constructor(private request: typeof fetch = fetch.bind(globalThis)) {}

  async json(url: URL, signal: AbortSignal): Promise<unknown> {
    return JSON.parse(await this.text(url, signal));
  }

  async txt(name: string, signal: AbortSignal): Promise<string[][]> {
    const url = new URL("https://cloudflare-dns.com/dns-query");
    url.search = new URLSearchParams({ name, type: "TXT" }).toString();
    const { value, errors } = validateDns(await this.json(url, signal));
    if (errors || (value.Status !== 0 && value.Status !== 3)) {
      throw new Error("DNS lookup failed.");
    }
    return (value.Answer ?? []).filter((answer) => answer.type === 16).map(
      ({ data }) => {
        const parts: string[] = [];
        let offset = 0;
        // DoH JSON represents a TXT RR as one or more DNS-escaped quoted strings.
        for (const match of data.matchAll(/"((?:[^"\\]|\\(?:\d{3}|.))*)"/g)) {
          if (data.slice(offset, match.index).trim()) {
            throw new Error("Invalid DNS TXT answer.");
          }
          parts.push(
            match[1].replace(
              /\\(\d{3}|.)/g,
              (_, escaped: string) =>
                /^\d{3}$/.test(escaped)
                  ? String.fromCharCode(Number(escaped))
                  : escaped,
            ),
          );
          offset = match.index + match[0].length;
        }
        if (!parts.length || data.slice(offset).trim()) {
          throw new Error("Invalid DNS TXT answer.");
        }
        return parts;
      },
    );
  }

  async text(url: URL, signal: AbortSignal): Promise<string> {
    publicUrl(url);
    signal.throwIfAborted();
    const key = url.href;
    const cached = this.#cache.get(key);
    if (cached && cached.expires > Date.now()) return cached.text;
    if (cached) {
      this.#cache.delete(key);
      this.#bytes -= cached.bytes;
    }
    const timeout = AbortSignal.any([signal, AbortSignal.timeout(15_000)]);
    for (let attempt = 0; attempt < 2; attempt++) {
      const response = await this.request(url, {
        signal: timeout,
        credentials: "omit",
        mode: "cors",
        redirect: "error",
        referrerPolicy: "no-referrer",
        headers: {
          accept: url.origin === "https://cloudflare-dns.com"
            ? "application/dns-json"
            : "application/json, text/plain",
        },
      });
      if (!response.ok) {
        await response.body?.cancel();
        if (
          attempt === 0 && (response.status === 429 || response.status === 503)
        ) {
          const seconds = Number(response.headers.get("retry-after") ?? "1");
          if (Number.isFinite(seconds) && seconds >= 0 && seconds <= 2) {
            await new Promise<void>((resolve) =>
              setTimeout(resolve, seconds * 1000)
            );
            timeout.throwIfAborted();
            continue;
          }
        }
        throw new Error(`${url.hostname} returned HTTP ${response.status}.`);
      }
      if (!response.body) {
        throw new Error(`${url.hostname} returned an empty response.`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8", { fatal: true });
      let bytes = 0;
      let text = "";
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          bytes += value.length;
          if (bytes > 1_048_576) {
            throw new Error("Upstream response exceeds 1 MiB.");
          }
          text += decoder.decode(value, { stream: true });
        }
        text += decoder.decode();
      } finally {
        await reader.cancel();
        reader.releaseLock();
      }
      while (this.#cache.size >= 256 || this.#bytes + bytes > 8_388_608) {
        const oldest = this.#cache.keys().next().value!;
        this.#bytes -= this.#cache.get(oldest)!.bytes;
        this.#cache.delete(oldest);
      }
      // Another request may have populated this key while this one was in flight.
      this.#bytes -= this.#cache.get(key)?.bytes ?? 0;
      this.#cache.set(key, { text, bytes, expires: Date.now() + 30_000 });
      this.#bytes += bytes;
      return text;
    }
    throw new Error("Upstream request failed.");
  }
}
